import React, { useState, useEffect } from 'react';
import { FiX, FiInfo } from 'react-icons/fi';
import { FaLink, FaImage } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import InfoModal from './InfoModal';

const ProfileModal = ({ isOpen, onClose, onSubmit, editingProfile }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    storeUrl: '',
    storeImageUrl: ''
  });
  const [showStoreUrlInfo, setShowStoreUrlInfo] = useState(false);
  const [showImageUrlInfo, setShowImageUrlInfo] = useState(false);

  useEffect(() => {
    if (editingProfile) {
      setFormData({
        name: editingProfile.name || '',
        description: editingProfile.description || '',
        storeUrl: editingProfile.storeUrl || '',
        storeImageUrl: editingProfile.storeImageUrl || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        storeUrl: '',
        storeImageUrl: ''
      });
    }
  }, [editingProfile, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ 
      name: '', 
      description: '',
      storeUrl: '',
      storeImageUrl: ''
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 transition-opacity bg-black bg-opacity-50 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left 
              overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle 
              sm:max-w-lg sm:w-full relative z-[70]"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {editingProfile ? 'Edit Business Profile' : 'Add Business Profile'}
                </h3>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FiX className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., My Store, Online Shop"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of your business..."
                    rows="3"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors resize-none"
                  />
                </div>

                {/* Store URL */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FaLink className="mr-2 text-gray-500" />
                    Store URL (Optional)
                    <button
                      type="button"
                      onClick={() => setShowStoreUrlInfo(true)}
                      className="ml-2 p-1 hover:bg-teal-100 dark:hover:bg-teal-900 rounded-full transition-colors"
                    >
                      <FiInfo className="h-4 w-4 text-teal-500 dark:text-teal-400 cursor-pointer animate-pulse" />
                    </button>
                  </label>
                  <input
                    type="url"
                    placeholder="https://www.ebay.com/usr/your-store"
                    value={formData.storeUrl}
                    onChange={(e) => setFormData({ ...formData, storeUrl: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors"
                  />
                </div>

                {/* Store Image URL */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FaImage className="mr-2 text-gray-500" />
                    Store Logo/Image URL (Optional)
                    <button
                      type="button"
                      onClick={() => setShowImageUrlInfo(true)}
                      className="ml-2 p-1 hover:bg-teal-100 dark:hover:bg-teal-900 rounded-full transition-colors"
                    >
                      <FiInfo className="h-4 w-4 text-teal-500 dark:text-teal-400 cursor-pointer animate-pulse" />
                    </button>
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/logo.png"
                    value={formData.storeImageUrl}
                    onChange={(e) => setFormData({ ...formData, storeImageUrl: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors"
                  />
                  {/* Image Preview */}
                  {formData.storeImageUrl && (
                    <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Preview:</p>
                      <img
                        src={formData.storeImageUrl}
                        alt="Store logo preview"
                        className="max-h-32 rounded-lg object-contain mx-auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <p className="text-xs text-red-600 dark:text-red-400 text-center mt-2" style={{display: 'none'}}>
                        ⚠️ Image failed to load. Please check the URL.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 
                      dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 
                      text-white rounded-lg hover:shadow-lg transition-all"
                  >
                    {editingProfile ? 'Update Profile' : 'Create Profile'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Info Modals */}
      <InfoModal
        isOpen={showStoreUrlInfo}
        onClose={() => setShowStoreUrlInfo(false)}
        title="🔗 Store URL"
        content={
          <div>
            <p className="font-semibold mb-2">Paste your online store or shop link</p>
            <p className="text-sm text-white/90">
              This can be your eBay store, Etsy shop, website, or any online storefront.
            </p>
            <p className="text-sm text-white/90 mt-2">
              Example: <span className="font-mono text-yellow-300">https://www.ebay.com/usr/your-store</span>
            </p>
          </div>
        }
      />

      <InfoModal
        isOpen={showImageUrlInfo}
        onClose={() => setShowImageUrlInfo(false)}
        title="🖼️ Store Logo/Image URL"
        content={
          <div>
            <p className="font-semibold mb-3">How to get image URL:</p>
            <ol className="list-decimal list-inside space-y-2 text-white/95">
              <li>Right-click on your logo or banner image</li>
              <li>Select <span className="font-semibold text-yellow-300">"Copy Image Address"</span></li>
              <li>Paste the URL here ✨</li>
            </ol>
            <p className="text-sm text-white/90 mt-4">
              The image will be displayed as your store logo on profile cards.
            </p>
          </div>
        }
      />
    </AnimatePresence>
  );
};

export default ProfileModal;

